// An issue's runs, compactly: the checks, verifications, investigations and
// reviews that agents recorded on it, newest first. A line says where each
// stands, what it is and how long ago it last moved; opening it shows its
// summary and its evidence. The latest few show, the others on request. The
// pages write no run: agents do, with `start_run`, `update_run` and
// `finish_run`. A run that lasts and has not been heard from is marked, as
// today's pages mark one.
import { useMemo, useState } from 'react'
import { Link } from 'react-router-dom'
import type { Issue, Run, RunEvidence, RunKind, RunStatus } from '../api/types'
import { SectionHeader } from '../components/layout'
import { Time } from '../components/Time'
import { Markdown } from '../markdown/Markdown'
import { useBoardState } from '../store/context'
import { actorName } from '../vocab'

/** How many runs show before the others are asked for. */
export const RUNS_SHOWN = 5
const MINUTE = 60_000

export const RUN_STATUS_NAMES: Record<RunStatus, string> = {
  running: 'Running',
  waiting: 'Waiting',
  passed: 'Passed',
  failed: 'Failed',
  cancelled: 'Cancelled',
}

export const RUN_KIND_NAMES: Record<RunKind, string> = {
  session: 'Session',
  check: 'Check',
  verify: 'Verification',
  investigate: 'Investigation',
  review: 'Review',
}

/** The runs on an issue, newest first. */
export function runsOf(runs: ReadonlyMap<string, Run>, issueId: string): Run[] {
  return [...runs.values()].filter((r) => r.issue === issueId).sort((a, b) => b.started_at.localeCompare(a.started_at) || b.id.localeCompare(a.id))
}

/**
 * Whether a run that lasts has been silent too long: no heartbeat for 30
 * minutes, or, for a waiting session, a day.
 */
export function isStale(run: Run, now: number): boolean {
  if (run.status !== 'running' && run.status !== 'waiting') return false
  const allowed = run.status === 'waiting' && run.kind === 'session' ? 24 * 60 * MINUTE : 30 * MINUTE
  const heard = Date.parse(run.updated_at)
  return !Number.isNaN(heard) && heard < now - allowed
}

function EvidenceItem({ item }: { item: RunEvidence }) {
  const text = item.value ? <>{item.label}: {item.value}</> : item.label
  if (!item.url) return <li>{text}</li>
  return <li>{item.url.startsWith('/') ? <Link to={item.url}>{text}</Link> : <a href={item.url} target="_blank" rel="noreferrer">{text}</a>}</li>
}

function RunLine({ run }: { run: Run }) {
  const stale = isStale(run, Date.now())
  const ended = run.ended_at !== null
  const details = run.summary.trim() !== '' || run.evidence.length > 0 || run.sha !== null || run.cost_usd !== null
  const head = (
    <>
      <span className={`run-status is-${run.status}`}>{RUN_STATUS_NAMES[run.status] ?? run.status}</span>
      <span className="run-kind">{RUN_KIND_NAMES[run.kind] ?? run.kind}</span>
      <span className="run-title">{run.title}</span>
      {stale && <span className="tag tag-warn" title="No heartbeat for a while">Silent</span>}
      {run.evidence.length > 0 && <span className="run-evidence-count" title="Items of evidence">{run.evidence.length}</span>}
      <Time iso={run.ended_at ?? run.updated_at} className="run-time" />
    </>
  )
  if (!details) return <div className="run-row">{head}</div>
  return (
    <details className="run-details">
      <summary className="run-row">{head}</summary>
      <div className="run-body">
        <p className="muted run-meta">
          {actorName(run.actor)} started it
          {run.sha ? <> on <code>{run.sha.slice(0, 12)}</code></> : null}
          {run.cost_usd !== null ? <>; cost ${run.cost_usd.toFixed(2)}</> : null}
          {ended ? null : '; still going'}
        </p>
        {run.summary.trim() !== '' && <Markdown source={run.summary} />}
        {run.evidence.length > 0 && (
          <ul className="run-evidence" aria-label="Evidence">
            {run.evidence.map((item, i) => (
              <EvidenceItem key={i} item={item} />
            ))}
          </ul>
        )}
      </div>
    </details>
  )
}

/** The Runs section: nothing when the issue has none. */
export function IssueRuns({ issue }: { issue: Issue }) {
  const { runs } = useBoardState()
  const [all, setAll] = useState(false)
  const list = useMemo(() => runsOf(runs, issue.id), [runs, issue.id])
  if (list.length === 0) return null
  const shown = all ? list : list.slice(0, RUNS_SHOWN)
  return (
    <section className="section issue-runs" aria-label="Runs">
      <SectionHeader
        title="Runs"
        count={list.length}
        actions={
          list.length > RUNS_SHOWN ? (
            <button type="button" className="button button-small button-quiet" onClick={() => setAll(!all)}>
              {all ? 'Show fewer' : `Show all ${list.length}`}
            </button>
          ) : undefined
        }
      />
      <ul className="run-list">
        {shown.map((run) => (
          <li key={run.id}>
            <RunLine run={run} />
          </li>
        ))}
      </ul>
    </section>
  )
}
